import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, Button, Space, message, Spin, Tag, Breadcrumb, Popconfirm } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getProjectsByProtocolId, Project, deleteProject } from '../../services/api';
import './styles.css';

const { Title } = Typography;

const ProtocolProjects: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [protocolTitle, setProtocolTitle] = useState<string>('');
  const navigate = useNavigate();
  const { protocolId } = useParams<{ protocolId: string }>();
  const location = useLocation();
  const passedProtocolTitle = location.state?.protocolTitle;

  useEffect(() => {
    if (passedProtocolTitle) {
      setProtocolTitle(passedProtocolTitle);
    }
  }, [passedProtocolTitle]);

  const fetchProjects = async () => {
    if (!protocolId) {
      message.error('协议ID不存在');
      return;
    }

    try {
      setLoading(true);
      const response = await getProjectsByProtocolId(Number(protocolId));
      if (response.data.code === 0 && response.data.data) {
        setProjects(response.data.data);
        setProtocolTitle(passedProtocolTitle || `协议 #${protocolId}`);
      } else {
        message.error(response.data.msg || '获取项目列表失败');
      }
    } catch (error) {
      console.error('获取项目列表错误:', error);
      message.error('获取项目列表失败，请检查网络连接后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [protocolId, passedProtocolTitle]);

  const getStatusText = (status: number) => {
    switch (status) {
      case 0:
        return { text: '禁用', color: 'red' };
      case 1:
        return { text: '启用', color: 'green' };
      default:
        return { text: '未知', color: 'gray' };
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      const response = await deleteProject(id);
      
      if (response.data.code === 0) {
        message.success('删除成功');
        fetchProjects(); // 刷新列表
      } else {
        message.error(response.data.msg || '删除失败');
      }
    } catch (error) {
      console.error('删除项目错误:', error);
      message.error('删除失败，请检查网络连接后重试');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '项目ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '项目代码',
      dataIndex: 'projectCode',
      key: 'projectCode',
    },
    {
      title: '区域',
      dataIndex: 'region',
      key: 'region',
      width: 100,
    },
    {
      title: '创建时间',
      dataIndex: 'addTime',
      key: 'addTime',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_: any, record: Project) => (
        <Space size="middle">
          <a onClick={() => navigate(`/protocol-project-edit/${protocolId}/${record.id}`)}>编辑</a>
          <a onClick={() => navigate(`/devices/${record.id}`, { state: { projectName: record.name, region: record.region, protocolId: Number(protocolId) } })}>设备管理</a>
          <Popconfirm
            title="确定要删除此项目吗?"
            description="删除后无法恢复，请谨慎操作"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <a style={{ color: 'red' }}><DeleteOutlined /> 删除</a>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="protocol-projects-container">
      <Card className="protocol-projects-card">
        <div className="protocol-projects-header">
          <div>
            <Breadcrumb items={[
              { title: <a onClick={() => navigate('/projects')}>授权协议</a> },
              { title: protocolTitle }
            ]} />
            <Title level={4} style={{ marginTop: '8px' }}>
              {protocolTitle} - 所属项目列表
            </Title>
          </div>
          <Space wrap>
            <Button 
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/protocol-project-create/${protocolId}`, { 
                state: { protocolTitle, protocolId } 
              })}
            >
              新建项目
            </Button>
            <Button 
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/projects')}
            >
              返回协议列表
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={fetchProjects}
            >
              刷新
            </Button>
          </Space>
        </div>
        
        <Spin spinning={loading}>
          <Table 
            dataSource={projects} 
            columns={columns} 
            rowKey="id"
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSize: 10,
              simple: window.innerWidth <= 576,
            }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default ProtocolProjects; 